// pages/interview/MocksTab.js
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Logo from "../../component/layout/Logo";
import slug from "../../utils/slug";
import Timer from "../../component/interview-tab/Timer";
import useRecordings from "../../hooks/useRecordings";
import { questions } from "../../data/questions";
import "../../styles/pages/interview/_mocks-tab.scss";

function pickRandomN(list, n) {
    const arr = [...list];
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr.slice(0, n);
}

export default function MocksTab() {
    const { getAccessTokenSilently } = useAuth0();
    const location = useLocation();
    const navigate = useNavigate();
    const [token, setToken] = useState(null);
    const [step, setStep] = useState("idle"); // idle | setup | recording | summary
    const [sessionId, setSessionId] = useState(null);
    const [mockQs, setMockQs] = useState([]);
    const [qIndex, setQIndex] = useState(0);

    const [isRunning, setIsRunning] = useState(false);
    const [isRecording, setIsRecording] = useState(false);

    const modalVideoRef = useRef(null);
    const streamRef = useRef(null);
    const recorderRef = useRef(null);
    const recordStartRef = useRef(null);
    const [mediaError, setMediaError] = useState(null);

    const [cameras, setCameras] = useState([]);
    const [mics, setMics] = useState([]);
    const [videoDeviceId, setVideoDeviceId] = useState("");
    const [audioDeviceId, setAudioDeviceId] = useState("");

    const [micOpen, setMicOpen] = useState(false);
    const [micLevel, setMicLevel] = useState(0);
    const micStreamRef = useRef(null);
    const audioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const rafRef = useRef(null);

    const currentQ = mockQs[qIndex] || null;

    const mockSlug = useMemo(
        () => (sessionId ? `mock-${sessionId}` : null),
        [sessionId]
    );

    const {
        items: mockRecordings,
        upload,
        remove,
    } = useRecordings({
        token,
        questionSlug: mockSlug,
    });

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const accessToken = await getAccessTokenSilently({
                    authorizationParams: {
                        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                    },
                });
                if (mounted) setToken(accessToken);
            } catch (err) {
                // ignore token errors; mock uploads will be disabled
            }
        })();
        return () => {
            mounted = false;
        };
    }, [getAccessTokenSilently]);

    const attachStream = useCallback((stream) => {
        if (!modalVideoRef.current) return;
        try {
            modalVideoRef.current.srcObject = stream;
            modalVideoRef.current.play?.().catch(() => {});
        } catch {}
    }, []);

    const stopStream = useCallback(() => {
        const s = streamRef.current;
        if (s) {
            s.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        if (modalVideoRef.current) modalVideoRef.current.srcObject = null;
    }, []);

    const ensureStream = useCallback(async () => {
        if (streamRef.current) return streamRef.current;

        if (!navigator?.mediaDevices?.getUserMedia) {
            setMediaError("Camera/microphone not supported by this browser.");
            return null;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: videoDeviceId
                    ? { deviceId: { exact: videoDeviceId } }
                    : true,
                audio: audioDeviceId
                    ? { deviceId: { exact: audioDeviceId } }
                    : true,
            });

            streamRef.current = stream;
            attachStream(stream);
            setMediaError(null);
            return stream;
        } catch (err) {
            console.error("getUserMedia error:", err);
            setMediaError(
                err?.message || "Failed to access camera/microphone."
            );
            return null;
        }
    }, [attachStream, videoDeviceId, audioDeviceId]);

    const startMock = useCallback(() => {
        const picked = pickRandomN(questions, 5);
        setMockQs(picked);
        setQIndex(0);
        setSessionId(String(Date.now()));
        setStep("setup");
    }, []);

    useEffect(() => {
        if (location.state?.autostart && step === "idle") {
            startMock();
            navigate("/dashboard/mocks", { replace: true, state: {} });
        }
    }, [location.state, navigate, startMock, step]);

    const closeAll = useCallback(() => {
        setStep("idle");
        setMockQs([]);
        setQIndex(0);
        setSessionId(null);
        setIsRecording(false);
        setIsRunning(false);
        setMicOpen(false);
        stopStream();
    }, [stopStream]);

    // enumerate devices when setup opens
    useEffect(() => {
        if (step !== "setup" || !navigator.mediaDevices?.enumerateDevices)
            return;

        navigator.mediaDevices.enumerateDevices().then((list) => {
            const vids = list.filter((d) => d.kind === "videoinput");
            const auds = list.filter((d) => d.kind === "audioinput");
            setCameras(vids);
            setMics(auds);

            if (!videoDeviceId && vids[0]?.deviceId)
                setVideoDeviceId(vids[0].deviceId);
            if (!audioDeviceId && auds[0]?.deviceId)
                setAudioDeviceId(auds[0].deviceId);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    // preview stream in setup
    useEffect(() => {
        if (step === "setup") ensureStream();
        else stopStream();
    }, [step, ensureStream, stopStream]);

    // mic meter
    useEffect(() => {
        if (!micOpen) {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
            micStreamRef.current?.getTracks()?.forEach((t) => t.stop());
            micStreamRef.current = null;
            audioCtxRef.current?.close?.();
            audioCtxRef.current = null;
            setMicLevel(0);
            return;
        }

        let mounted = true;

        (async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: true,
                });
                micStreamRef.current = stream;

                const Ctx = window.AudioContext || window.webkitAudioContext;
                const ctx = new Ctx();
                audioCtxRef.current = ctx;

                const source = ctx.createMediaStreamSource(stream);
                const analyser = ctx.createAnalyser();
                analyser.fftSize = 2048;
                analyser.smoothingTimeConstant = 0.8;
                source.connect(analyser);
                analyserRef.current = analyser;

                const buf = new Uint8Array(analyser.fftSize);

                const loop = () => {
                    analyser.getByteTimeDomainData(buf);
                    let sum = 0;
                    for (let i = 0; i < buf.length; i++) {
                        const v = (buf[i] - 128) / 128;
                        sum += v * v;
                    }
                    const rms = Math.sqrt(sum / buf.length);
                    if (mounted) setMicLevel(Math.min(1, rms * 2));
                    rafRef.current = requestAnimationFrame(loop);
                };
                loop();
            } catch (err) {
                console.error("Mic test failed:", err);
                setMicOpen(false);
            }
        })();

        return () => {
            mounted = false;
        };
    }, [micOpen]);

    const beginSession = useCallback(async () => {
        const stream = await ensureStream();
        if (!stream) return;
        setStep("recording");
        setIsRunning(false);
        setIsRecording(false);
    }, [ensureStream]);

    const startRecording = useCallback(async () => {
        const stream = streamRef.current || (await ensureStream());
        if (!stream || !currentQ || !mockSlug) return;
        if (!token) {
            setMediaError("Please log in to save mock recordings.");
            return;
        }

        const chunks = [];
        const mr = new MediaRecorder(stream);
        recorderRef.current = mr;

        mr.ondataavailable = (e) => chunks.push(e.data);

        mr.onstop = async () => {
            const blob = new Blob(chunks, { type: "video/webm" });
            const qSlug =
                slug(currentQ.id ?? currentQ.question) || `q-${qIndex + 1}`;
            const startedAt = recordStartRef.current;
            const durationMs = startedAt
                ? Math.max(0, Date.now() - startedAt)
                : null;

            await upload({
                blob,
                filename: `${mockSlug}-${qIndex + 1}-${qSlug}-${Date.now()}.webm`,
                question_slug: mockSlug, // store mock recordings under mockSlug
                duration_ms: durationMs,
                evaluation_style: currentQ?.evaluationStyle,
                evaluation_criteria: currentQ?.evaluationCriteria
                    ? JSON.stringify(currentQ.evaluationCriteria)
                    : null,
            });

            const next = qIndex + 1;
            if (next < mockQs.length) {
                setQIndex(next);
                setIsRunning(false);
                setIsRecording(false);
            } else {
                setStep("summary");
                setIsRunning(false);
                setIsRecording(false);
                stopStream();
            }
        };

        mr.start();
        recordStartRef.current = Date.now();
        setIsRecording(true);
        setIsRunning(true);
    }, [
        ensureStream,
        currentQ,
        mockSlug,
        qIndex,
        mockQs.length,
        upload,
        sessionId,
        stopStream,
        token,
    ]);

    const stopRecordingNow = useCallback(() => {
        const mr = recorderRef.current;
        if (mr && mr.state !== "inactive") mr.stop();
        setIsRecording(false);
        setIsRunning(false);
    }, []);

    const toggleRecording = useCallback(() => {
        if (isRecording) stopRecordingNow();
        else startRecording();
    }, [isRecording, startRecording, stopRecordingNow]);

    return (
        <div className="mocks-tab">
            <div className="tab-header">Mocks</div>
            <div className="tab-subheading">
                5 random questions. Record each answer. Saved to your account.
            </div>

            {step === "idle" && (
                <button className="btn btn-primary" onClick={startMock}>
                    Start a 5-question mock
                </button>
            )}

            {step === "setup" && (
                <aside
                    className="modal-recording mock-setup"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Mock interview setup"
                >
                    <div className="container">
                        <div className="mock-setup__header">
                            {/* Choose your audio and video settings */}
                            {/* Mock interview setup */}
                            Adjust your camera and mic before you begin.
                        </div>
                        <div className="recording-shell">
                            <div className="recording-left">
                                <div className="mock-preview-card">
                                    <div className="webcam">
                                        <video
                                            ref={modalVideoRef}
                                            autoPlay
                                            playsInline
                                            muted
                                        />
                                        {mediaError && (
                                            <div className="video-error">
                                                {mediaError}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mock-preview-footer">
                                        <div className="mock-preview-toggle">
                                            <span className="mock-toggle-pill">
                                                Live preview
                                            </span>
                                        </div>
                                        <button
                                            className="btn-exit"
                                            onClick={closeAll}
                                            aria-label="Close"
                                        >
                                            <span
                                                className="mock-icon mock-icon--exit"
                                                aria-hidden="true"
                                            />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="recording-right">
                                <div className="mock-settings-card">
                                    <div className="mock-settings-row">
                                        <div className="mock-settings-row__icon">
                                            <span
                                                className="mock-icon mock-icon--camera"
                                                aria-hidden="true"
                                            />
                                        </div>
                                        <div className="mock-settings-row__info">
                                            <div className="mock-settings-row__title">
                                                Camera
                                            </div>
                                            <div className="mock-settings-row__controls">
                                                <select
                                                    id="camera"
                                                    className="control"
                                                    value={videoDeviceId}
                                                    onChange={(e) =>
                                                        setVideoDeviceId(
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    {cameras.length ? (
                                                        cameras.map((d) => (
                                                            <option
                                                                key={d.deviceId}
                                                                value={
                                                                    d.deviceId
                                                                }
                                                            >
                                                                {d.label ||
                                                                    "Camera"}
                                                            </option>
                                                        ))
                                                    ) : (
                                                        <option>
                                                            Default camera
                                                        </option>
                                                    )}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mock-settings-row">
                                        <div className="mock-settings-row__icon">
                                            <span
                                                className="mock-icon mock-icon--mic"
                                                aria-hidden="true"
                                            />
                                        </div>
                                        <div className="mock-settings-row__info">
                                            <div className="mock-settings-row__title">
                                                Microphone
                                            </div>
                                            <div className="mock-settings-row__controls">
                                                <select
                                                    id="mic"
                                                    className="control"
                                                    value={audioDeviceId}
                                                    onChange={(e) =>
                                                        setAudioDeviceId(
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    {mics.length ? (
                                                        mics.map((d) => (
                                                            <option
                                                                key={d.deviceId}
                                                                value={
                                                                    d.deviceId
                                                                }
                                                            >
                                                                {d.label ||
                                                                    "Microphone"}
                                                            </option>
                                                        ))
                                                    ) : (
                                                        <option>
                                                            Default microphone
                                                        </option>
                                                    )}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mock-settings-actions">
                                        <button
                                            type="button"
                                            className="btn-cancel"
                                            onClick={closeAll}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-session"
                                            onClick={beginSession}
                                        >
                                            Start mock
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </aside>
            )}

            {step === "recording" && currentQ && (
                <aside
                    className="modal-recording mock-session-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Mock interview recording"
                >
                    <div className="container">
                        <div className="recording-shell">
                            <div className="recording-left">
                                <div className="recording-question">
                                    <div className="muted">
                                        Mock interview • Question {qIndex + 1} /{" "}
                                        {mockQs.length}
                                    </div>
                                    <div className="q-now">
                                        {currentQ.question}
                                    </div>
                                </div>
                            </div>

                            <div className="recording-right">
                                <div className="recording-card">
                                    <div className="recording-card__top">
                                        <Timer isRunning={isRunning} />
                                        <button
                                            className="btn-exit"
                                            onClick={closeAll}
                                            aria-label="Close"
                                        >
                                            <span
                                                className="mock-icon mock-icon--exit"
                                                aria-hidden="true"
                                            />
                                        </button>
                                    </div>

                                    <div className="webcam">
                                        <video
                                            ref={modalVideoRef}
                                            autoPlay
                                            playsInline
                                            muted
                                        />
                                    </div>

                                    <div className="recording-card__bottom">
                                        <button
                                            className={`btn btn-record ${isRecording ? "is-recording" : ""}`}
                                            onClick={toggleRecording}
                                            aria-pressed={isRecording}
                                        >
                                            <span
                                                className={`rec-dot ${isRecording ? "on" : ""}`}
                                                aria-hidden="true"
                                            />
                                            <span className="rec-label">
                                                {isRecording
                                                    ? "Recording..."
                                                    : "Start recording"}
                                            </span>
                                        </button>

                                        {isRecording && (
                                            <button
                                                className="btn btn-secondary"
                                                onClick={stopRecordingNow}
                                            >
                                                Finish question
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </aside>
            )}

            {step === "summary" && (
                <div className="mock-summary">
                    <h2>Mock complete 🎉</h2>
                    <p className="muted">
                        Recordings are saved to your account.
                    </p>

                    {mockRecordings?.length ? (
                        <ul className="mock-recordings">
                            {mockRecordings.map((r) => (
                                <li key={r.id ?? r.url}>
                                    <div className="meta">
                                        {r?.meta?.questionText ||
                                            "Mock recording"}
                                    </div>
                                    <video src={r.url} controls />
                                    {r.id && (
                                        <button
                                            className="btn btn-link"
                                            onClick={() => remove(r.id)}
                                        >
                                            Delete
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="muted">
                            No recordings returned — check your upload endpoint
                            response.
                        </p>
                    )}

                    <button className="btn btn-primary" onClick={startMock}>
                        Start another mock
                    </button>
                </div>
            )}
        </div>
    );
}
